import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

const formatAmount = (value) => {
  if (value === null || value === undefined) return '-';
  const numValue = Number(value);
  const roundedUp = Math.ceil(numValue);
  return roundedUp.toLocaleString('en-IN');
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch (e) {
    return dateString;
  }
};

export default function TaxSummaryTable(props) {
  const { data, isLoading } = props;
  const scrollableBodyRef = useRef(null);
  const fixedBodyRef = useRef(null);

  // Column widths
  const columnWidths = {
    srNo: '80px',
    employeeName: '200px',
    totalGross: '140px',
    standardDeduction: '160px',
    otherIncome: '130px',
    taxableIncome: '140px',
    calculatedIncomeTax: '170px',
    rebate87a: '200px',
    taxAfterRebate: '140px',
    cessAmount: '130px',
    totalTaxLiability: '150px',
    cumulativeTDS: '180px',
    incomeTaxDue: '140px',
    remainingMonths: '150px',
    monthlyTaxAmount: '160px',
    lastCalculatedMonth: '180px',
    lastCalculatedDate: '150px',
  };

  // Calculate widths for fixed and scrollable sections (3 fixed columns)
  const fixedColumnKeys = ['srNo', 'employeeName'];
  const fixedColumnWidths = fixedColumnKeys.map(key => columnWidths[key]);
  const fixedColumnsTotalWidth = fixedColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);
  
  const scrollableColumnKeys = Object.keys(columnWidths).filter(key => !fixedColumnKeys.includes(key));
  const scrollableColumnWidths = scrollableColumnKeys.map(key => columnWidths[key]);
  const scrollableColumnsTotalWidth = scrollableColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);

  // Column names
  const fixedColumnNames = ['Sr. No.', 'Employee Name'];
  const scrollableColumnNames = [
    'Total Gross',
    'Standard Deduction',
    'Other Income',
    'Net Taxable Income',
    'Tax on Income',
    'Rebate (87A)',
    'Tax After Rebate',
    'Cess Amount',
    'Total Tax Liability',
    'TDS Paid',
    'Income Tax Due',
    'Remaining Months',
    'Monthly Tax Amount',
    'Last Calculated Month',
    'Last Calculated Date',
  ];

  // Calculate totals
  const calculateTotals = () => {
    if (!data || data.length === 0) return {};
    
    const totals = {
      totalGross: 0,
      standardDeduction: 0,
      otherIncomeTotal: 0,
      taxableIncome: 0,
      calculatedIncomeTax: 0,
      rebate87a: 0,
      taxAfterRebate: 0,
      cessAmount: 0,
      totalTaxLiability: 0,
      cumulativeTDS: 0,
      incomeTaxDue: 0,
      monthlyTaxAmount: 0,
    };

    data.forEach((item) => {
      totals.totalGross += Number(item.total_gross) || 0;
      totals.standardDeduction += Number(item.standard_deduction) || 0;
      totals.otherIncomeTotal += Number(item.other_income) || 0;
      totals.taxableIncome += Number(item.taxable_income) || 0;
      totals.calculatedIncomeTax += Number(item.calculated_income_tax) || 0;
      totals.rebate87a += Number(item.rebate_under_section_87a) || 0;
      totals.taxAfterRebate += Number(item.tax_after_rebate) || 0;
      totals.cessAmount += Number(item.cess_amount) || 0;
      totals.totalTaxLiability += Number(item.total_tax_liability) || 0;
      totals.cumulativeTDS += Number(item.cumulative_tax_deducted) || 0;
      totals.incomeTaxDue += Number(item.income_tax_due) || 0;
      totals.monthlyTaxAmount += Number(item.monthly_tax_amount) || 0;
    });

    return totals;
  };

  const totals = calculateTotals();

  // Sync scroll for header and body
  useEffect(() => {
    const scrollableHeader = document.getElementById('scrollable-header-container');
    const scrollableBody = scrollableBodyRef.current;
    const fixedBody = fixedBodyRef.current;

    const syncScroll = () => {
      if (scrollableBody && scrollableHeader) {
        // Sync horizontal scroll for header
        scrollableHeader.scrollLeft = scrollableBody.scrollLeft;
      }
      if (scrollableBody && fixedBody) {
        // Move fixed columns up/down based on scroll position
        fixedBody.style.transform = `translateY(-${scrollableBody.scrollTop}px)`;
      }
    };

    scrollableBody?.addEventListener('scroll', syncScroll);

    return () => {
      scrollableBody?.removeEventListener('scroll', syncScroll);
    };
  }, [isLoading]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        No data available.
      </Typography>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      height: '500px', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      overflowX: 'hidden',
      maxWidth: '100%'
    }}>
      
      {/* Header Container */}
      <Box 
        sx={{ 
          display: 'flex',
          position: 'sticky', 
          top: 0, 
          zIndex: 10,
          background: '#fff',
          flexShrink: 0,
          borderBottom: '2px solid #dee2e6',
          overflow: 'hidden'
        }}
      >
        {/* Fixed Header Columns */}
        <Box
          sx={{
            width: `${fixedColumnsTotalWidth}px`,
            flexShrink: 0,
            background: '#212529',
            color: 'white'
          }}
        >
          <table style={{ 
            display: 'table',
            overflow: 'visible',
            whiteSpace: 'normal',
            width: '100%', 
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            margin: 0
          }} className="table table-dark">
            <thead>
              <tr>
                {fixedColumnNames.map((name, index) => (
                  <th 
                    key={index}
                    style={{ 
                      width: fixedColumnWidths[index], 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: index === 1 ? 'left' : 'center',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={name} placement="top" arrow>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </Box>

        {/* Scrollable Header Columns */}
        <Box
          id="scrollable-header-container"
          sx={{ 
            flex: 1,
            overflowX: 'hidden',
            overflowY: 'hidden',
            background: '#212529',
            color: 'white'
          }}
        >
          <table style={{ 
            display: 'table',
            overflow: 'visible',
            whiteSpace: 'normal',
            width: `${scrollableColumnsTotalWidth}px`, 
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            margin: 0
          }} className="table table-dark">
            <thead>
              <tr>
                {scrollableColumnNames.map((name, index) => (
                  <th 
                    key={index}
                    style={{ 
                      width: scrollableColumnWidths[index], 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: index >= 10 ? 'left' : 'right',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={name} placement="top" arrow>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </Box>
      </Box>

      {/* Body Container */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1, 
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Fixed Body Columns - Wrapper that stays in place */}
        <Box
          sx={{ 
            width: `${fixedColumnsTotalWidth}px`,
            flexShrink: 0,
            background: '#fff',
            borderRight: '2px solid #dee2e6',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Inner container that moves with scroll */}
          <Box
            ref={fixedBodyRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              overflow: 'hidden'
            }}
          >
            <table style={{ 
              display: 'table',
              overflow: 'visible',
              whiteSpace: 'normal',
              width: '100%', 
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
              margin: 0
            }} className="table">
              <tbody>
                {data.map((item, i) => (
                  <tr key={i} style={{ height: '42px' }}>
                    <td style={{ 
                      width: columnWidths.srNo, 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: 'center',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      {i + 1}
                    </td>
                    <td style={{ 
                      width: columnWidths.employeeName, 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: 'left',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      {item.displayName || (item.first_name && item.last_name 
                        ? `${item.first_name} ${item.last_name}` 
                        : item.employee_id ? `Employee ID: ${item.employee_id}` : 'N/A')}
                    </td>
                  </tr>
                ))}

                {/* Totals Row in Fixed Columns */}
                {data.length > 0 && (
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', height: '42px' }}>
                    <td style={{ 
                      width: columnWidths.srNo, 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: 'center',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #dee2e6'
                    }}>Total</td>
                    <td style={{ 
                      width: columnWidths.employeeName, 
                      padding: '8px', 
                      boxSizing: 'border-box', 
                      textAlign: 'center',
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #dee2e6'
                    }}>Total</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Scrollable Body Columns - only horizontal scrollbar */}
        <Box
          ref={scrollableBodyRef}
          sx={{ 
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'auto',
            background: '#fff'
          }}
        >
          <table style={{ 
            display: 'table',
            overflow: 'visible',
            whiteSpace: 'normal',
            width: `${scrollableColumnsTotalWidth}px`, 
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            margin: 0
          }} className="table">
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ height: '42px' }}>
                  <td style={{ 
                    width: columnWidths.totalGross, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.total_gross)}</td>
                  <td style={{ 
                    width: columnWidths.standardDeduction, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.standard_deduction)}</td>
                  <td style={{ 
                    width: columnWidths.otherIncome, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>
                    {formatAmount(item.other_income)}
                  </td>
                  <td style={{ 
                    width: columnWidths.taxableIncome, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.taxable_income)}</td>
                  <td style={{ 
                    width: columnWidths.calculatedIncomeTax, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.calculated_income_tax)}</td>
                  <td style={{ 
                    width: columnWidths.rebate87a, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.rebate_under_section_87a)}</td>
                  <td style={{ 
                    width: columnWidths.taxAfterRebate, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.tax_after_rebate)}</td>
                  <td style={{ 
                    width: columnWidths.cessAmount, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.cess_amount)}</td>
                  <td style={{ 
                    width: columnWidths.totalTaxLiability, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.total_tax_liability)}</td>
                  <td style={{ 
                    width: columnWidths.cumulativeTDS, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.cumulative_tax_deducted)}</td>
                  <td style={{ 
                    width: columnWidths.incomeTaxDue, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.income_tax_due)}</td>
                  <td style={{ 
                    width: columnWidths.remainingMonths, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{item.remaining_months ?? '-'}</td>
                  <td style={{ 
                    width: columnWidths.monthlyTaxAmount, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(item.monthly_tax_amount)}</td>
                  <td style={{ 
                    width: columnWidths.lastCalculatedMonth, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{item.last_calculated_month || '-'}</td>
                  <td style={{ 
                    width: columnWidths.lastCalculatedDate, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatDate(item.last_calculated_date)}</td>
                </tr>
              ))}

              {/* Totals Row in Scrollable Columns */}
              {data.length > 0 && (
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', height: '42px' }}>
                  <td style={{ 
                    width: columnWidths.totalGross, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.totalGross)}</td>
                  <td style={{ 
                    width: columnWidths.standardDeduction, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.standardDeduction)}</td>
                  <td style={{ 
                    width: columnWidths.otherIncome, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.otherIncomeTotal)}</td>
                  <td style={{ 
                    width: columnWidths.taxableIncome, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.taxableIncome)}</td>
                  <td style={{ 
                    width: columnWidths.calculatedIncomeTax, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.calculatedIncomeTax)}</td>
                  <td style={{ 
                    width: columnWidths.rebate87a, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.rebate87a)}</td>
                  <td style={{ 
                    width: columnWidths.taxAfterRebate, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.taxAfterRebate)}</td>
                  <td style={{ 
                    width: columnWidths.cessAmount, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.cessAmount)}</td>
                  <td style={{ 
                    width: columnWidths.totalTaxLiability, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.totalTaxLiability)}</td>
                  <td style={{ 
                    width: columnWidths.cumulativeTDS, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.cumulativeTDS)}</td>
                  <td style={{ 
                    width: columnWidths.incomeTaxDue, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.incomeTaxDue)}</td>
                  <td style={{ 
                    width: columnWidths.remainingMonths, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}></td>
                  <td style={{ 
                    width: columnWidths.monthlyTaxAmount, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'right',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}>{formatAmount(totals.monthlyTaxAmount)}</td>
                  <td style={{ 
                    width: columnWidths.lastCalculatedMonth, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}></td>
                  <td style={{ 
                    width: columnWidths.lastCalculatedDate, 
                    padding: '8px', 
                    boxSizing: 'border-box', 
                    textAlign: 'left',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6'
                  }}></td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>
    </div>
  );
}

TaxSummaryTable.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
};